import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { ApiResponse } from 'src/app/models/api-response.model';
import { SnackService } from 'src/app/services/snack.service';
import { environment } from 'src/environments/environment';
import { waitPhrase } from './benefits-check-result-view';
import { BenefitsCheckResultDialogComponent } from './benefits-check-result-dialog.component';
import {
  BenefitsCheckAccess,
  ManualBenefitsCheckResponse,
  ManualBenefitsCheckSummary,
} from './manual-benefits-check.model';

export const NO_ACCESS_MESSAGE = "You don't have access to run benefits checks";
export const TURNED_OFF_MESSAGE = 'Benefits checks are turned off';
export const GENERIC_ERROR_MESSAGE = "Couldn't run the benefits check. Please try again.";

/** Plain-words text for a failed call. Never includes anything from the patient. */
export const benefitsCheckErrorMessage = (error: unknown): string => {
  const status = (error as HttpErrorResponse)?.status;
  if (status === 403) {
    const serverMessage = (error as HttpErrorResponse)?.error?.message;
    return typeof serverMessage === 'string' && /turned off|disabled|not enabled/i.test(serverMessage)
      ? TURNED_OFF_MESSAGE
      : NO_ACCESS_MESSAGE;
  }
  if (status === 429) {
    const seconds = Number((error as HttpErrorResponse).headers?.get?.('Retry-After'));
    return `You're running checks too quickly. Try again in ${waitPhrase(seconds)}.`;
  }
  return GENERIC_ERROR_MESSAGE;
};

@Injectable({ providedIn: 'root' })
export class ManualBenefitsCheckService {
  /**
   * Whether to show the Run check buttons: the backend says the feature is on AND this user is on its
   * allow-list. Asked once and cached for the session (the request starts on first subscribe). Any
   * failure hides the buttons (fail closed) and logs nothing. The POST still enforces access itself
   * (HTTP 403), so this only decides whether the buttons are drawn.
   */
  readonly canRun$: Observable<boolean> = this.http
    .get<ApiResponse<BenefitsCheckAccess>>(`${environment.welbyEndpoint}/api/v1/core/benefits-check/access`)
    .pipe(
      map((res) => res.data?.enabled === true && res.data?.allowed === true),
      catchError(() => of(false)),
      shareReplay(1)
    );

  private readonly baseUrl = `${environment.welbyEndpoint}/api/v1/core/patients`;

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private snack: SnackService
  ) {}

  /** POST with no body; unwraps the standard envelope. */
  run(patientId: string): Observable<ManualBenefitsCheckResponse> {
    return this.http
      .post<ApiResponse<ManualBenefitsCheckResponse>>(
        `${this.baseUrl}/${encodeURIComponent(patientId)}/benefits-check`,
        null
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Runs the check, opens the result dialog and emits the new benefits_check summary as soon as the
   * answer arrives (so the caller can update its badge right away). Emits null when there is nothing
   * to update, including after a failure, which is shown as a snackbar here.
   */
  runAndShow(patientId: string): Observable<ManualBenefitsCheckSummary | null> {
    return this.run(patientId).pipe(
      tap((response) => this.openResult(response)),
      map((response) => response.benefitsCheck ?? null),
      catchError((error) => {
        this.snack.genericSnackBar(benefitsCheckErrorMessage(error), ['error-snackbar'], 6000);
        return of(null);
      })
    );
  }

  private openResult(response: ManualBenefitsCheckResponse): void {
    this.dialog.open(BenefitsCheckResultDialogComponent, {
      data: response,
      width: '28rem',
      maxWidth: '95vw',
      autoFocus: 'dialog',
      restoreFocus: true,
      ariaLabelledBy: 'benefits-check-result-title',
    });
  }
}
